'use client';

import React, { useState, useEffect, useRef } from 'react';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';

interface LiveEvent {
  id: string;
  time: string;
  center: string;
  centerColor: string;
  event: string;
  detail: string;
  dot: string;
  isNew?: boolean;
}

function formatTime(iso: string) {
  return new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

function buildEventFromPayload(table: string, eventType: string, record: any): LiveEvent {
  const now = new Date().toISOString();
  if (table === 'hr_employees') {
    if (eventType === 'INSERT') {
      return {
        id: `${record.id}-insert`,
        time: formatTime(now),
        center: 'HR',
        centerColor: 'var(--primary)',
        event: 'New employee added',
        detail: `${record.first_name ?? ''} ${record.last_name ?? ''} · ${record.job_title ?? ''} · ${record.department ?? ''}`.trim(),
        dot: 'var(--color-green)',
        isNew: true,
      };
    }
    if (eventType === 'UPDATE') {
      return {
        id: `${record.id}-update-${Date.now()}`,
        time: formatTime(now),
        center: 'HR',
        centerColor: 'var(--primary)',
        event: 'Employee record updated',
        detail: `${record.first_name ?? ''} ${record.last_name ?? ''} · ${record.employee_status ?? ''}`,
        dot: 'var(--primary)',
        isNew: true,
      };
    }
  }
  if (table === 'payroll_records') {
    return {
      id: `${record.id}-payroll-${Date.now()}`,
      time: formatTime(now),
      center: 'PAYROLL',
      centerColor: 'var(--color-amber)',
      event: eventType === 'INSERT' ? 'Payroll record created' : 'Payroll liability updated',
      detail: `${record.first_name ?? ''} ${record.last_name ?? ''} · ${record.job_title ?? ''} · £${Number(record.annual_salary ?? 0).toLocaleString('en-GB')} p.a.`,
      dot: 'var(--color-amber)',
      isNew: true,
    };
  }
  if (table === 'hr_audit_log') {
    const actionLabel = (record.action_type ?? '').replace(/_/g, ' ');
    return {
      id: `${record.id}-audit`,
      time: formatTime(now),
      center: 'AUDIT',
      centerColor: 'var(--color-purple)',
      event: actionLabel.charAt(0).toUpperCase() + actionLabel.slice(1),
      detail: `${record.performed_by_name ?? 'System'} · ${record.entity_label ?? record.entity_id ?? ''}`,
      dot: 'var(--color-purple)',
      isNew: true,
    };
  }
  return {
    id: `unknown-${Date.now()}`,
    time: formatTime(now),
    center: 'SYS',
    centerColor: 'var(--muted-foreground)',
    event: `${table} ${eventType.toLowerCase()}`,
    detail: '',
    dot: 'var(--muted-foreground)',
    isNew: true,
  };
}

interface Props {
  companyId: string | null;
}

export default function RealtimeDashboard({ companyId }: Props) {
  const [liveEvents, setLiveEvents] = useState<LiveEvent[]>([]);
  const [connected, setConnected] = useState(false);
  const [newCount, setNewCount] = useState(0);
  const channelRef = useRef<any>(null);

  useEffect(() => {
    if (!companyId) return;
    const supabase = createClient();

    const channel = supabase
      .channel(`dashboard-realtime-${companyId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'hr_employees', filter: `company_id=eq.${companyId}` },
        (payload) => {
          if (payload.eventType === 'DELETE') return;
          const ev = buildEventFromPayload('hr_employees', payload.eventType, payload.new);
          setLiveEvents((prev) => [ev, ...prev].slice(0, 50));
          setNewCount((c) => c + 1);
        }
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'payroll_records', filter: `company_id=eq.${companyId}` },
        (payload) => {
          if (payload.eventType === 'DELETE') return;
          const ev = buildEventFromPayload('payroll_records', payload.eventType, payload.new);
          setLiveEvents((prev) => [ev, ...prev].slice(0, 50));
          setNewCount((c) => c + 1);
        }
      )
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'hr_audit_log', filter: `company_id=eq.${companyId}` },
        (payload) => {
          const ev = buildEventFromPayload('hr_audit_log', 'INSERT', payload.new);
          setLiveEvents((prev) => [ev, ...prev].slice(0, 50));
          setNewCount((c) => c + 1);
        }
      )
      .subscribe((status) => {
        setConnected(status === 'SUBSCRIBED');
      });

    channelRef.current = channel;
    return () => {
      supabase.removeChannel(channel);
    };
  }, [companyId]);

  const clearNew = () => setNewCount(0);

  if (liveEvents.length === 0 && !connected) return null;

  return (
    <div className="card-elevated rounded-xl p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="relative">
            <div className="w-2 h-2 rounded-full" style={{ background: connected ? 'var(--color-green)' : 'var(--color-amber)' }} />
            {connected && (
              <div className="absolute inset-0 w-2 h-2 rounded-full animate-ping"
                style={{ background: 'var(--color-green)', opacity: 0.5 }} />
            )}
          </div>
          <h3 className="text-sm font-semibold text-card-foreground">Live Updates</h3>
          {newCount > 0 && (
            <button
              onClick={clearNew}
              className="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold transition-all"
              style={{ background: 'rgba(0,212,255,0.15)', color: 'var(--primary)', border: '1px solid rgba(0,212,255,0.3)' }}>
              {newCount} new
              <Icon name="XMarkIcon" size={10} />
            </button>
          )}
        </div>
        <span className="text-xs text-muted-foreground">{connected ? 'Real-time · No refresh needed' : 'Connecting…'}</span>
      </div>

      {liveEvents.length === 0 ? (
        <div className="flex items-center justify-center py-6 text-center">
          <div>
            <Icon name="BoltIcon" size={18} className="text-muted-foreground mx-auto mb-1" />
            <p className="text-xs text-muted-foreground">Listening for live changes…</p>
          </div>
        </div>
      ) : (
        <div className="space-y-0 max-h-64 overflow-y-auto">
          {liveEvents.map((ev, i) => (
            <div
              key={ev.id}
              className="flex gap-3 py-2.5 border-b border-border last:border-0 px-2 -mx-2 rounded-md transition-all"
              style={{
                background: ev.isNew && i === 0 ? 'rgba(0,212,255,0.04)' : undefined,
                borderLeft: ev.isNew && i === 0 ? '2px solid var(--primary)' : undefined,
              }}>
              <div className="flex flex-col items-center flex-shrink-0 pt-0.5">
                <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: ev.dot }} />
                {i < liveEvents.length - 1 && (
                  <span className="w-px flex-1 mt-1" style={{ background: 'var(--border)', minHeight: '20px', display: 'block' }} />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs font-semibold px-1.5 py-0.5 rounded font-mono"
                    style={{ background: 'rgba(0,0,0,0.3)', color: ev.centerColor, border: `1px solid ${ev.centerColor}33` }}>
                    {ev.center}
                  </span>
                  <span className="text-xs font-medium text-card-foreground">{ev.event}</span>
                  <span className="text-xs text-muted-foreground ml-auto">{ev.time}</span>
                </div>
                {ev.detail && <p className="text-xs text-muted-foreground mt-0.5 truncate">{ev.detail}</p>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
